import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import {
  OutputFormulaValue,
  outputFormulaValueKindLabel,
} from 'src/app/models/output-formulas/output-formula-value';

export interface FormulaMetaBadge {
  icon: string;
  label: string;
}

export const formulaMetaBadge = (formula: OutputFormula): FormulaMetaBadge => {
  const kindLabel = outputFormulaValueKindLabel(formula.value.kind);
  const icon = formula.value.kind.startsWith('range') ? 'format_list_numbered' : 'tune';
  return { icon, label: kindLabel };
};

export const formulaStillsSpecId = (value: OutputFormulaValue): string | null => {
  switch (value.kind) {
    case 'rangeStills':
      return value.stills.specId;
    case 'rangeBoth':
      return value.stills.specId;
    case 'evStills':
    case 'evBoth':
      return value.stillsSpecId;
    case 'rangeVideos':
    case 'evVideos':
      return null;
  }
};

export const formulaVideosSpecId = (value: OutputFormulaValue): string | null => {
  switch (value.kind) {
    case 'rangeVideos':
      return value.videos.specId;
    case 'rangeBoth':
      return value.videos.specId;
    case 'evVideos':
    case 'evBoth':
      return value.videosSpecId;
    case 'rangeStills':
    case 'evStills':
      return null;
  }
};

export const formulaStillsConventionId = (value: OutputFormulaValue): string | null => {
  switch (value.kind) {
    case 'rangeStills':
      return value.stills.conventionId;
    case 'rangeBoth':
      return value.stills.conventionId;
    case 'rangeVideos':
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return null;
  }
};

export const formulaVideosConventionId = (value: OutputFormulaValue): string | null => {
  switch (value.kind) {
    case 'rangeVideos':
      return value.videos.conventionId;
    case 'rangeBoth':
      return value.videos.conventionId;
    case 'rangeStills':
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return null;
  }
};

export const formulaEvConventionIds = (value: OutputFormulaValue): readonly string[] => {
  switch (value.kind) {
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return value.evConventionIds;
    case 'rangeStills':
    case 'rangeVideos':
    case 'rangeBoth':
      return [];
  }
};

export const buildNameLookup = <T extends { id: string; name: string }>(
  rows: readonly T[],
): Map<string, string> => new Map(rows.map((row) => [row.id, row.name]));

const UNKNOWN_NAME = '—';

export const lookupName = (map: Map<string, string>, id: string | null): string => {
  if (id === null) return UNKNOWN_NAME;
  return map.get(id) ?? UNKNOWN_NAME;
};

export const orderedEvConventionNames = (
  map: Map<string, string>,
  ids: readonly string[],
): string =>
  ids.map((id, index) => `${index + 1}. ${map.get(id) ?? UNKNOWN_NAME}`).join(' · ');

export interface FormulaResolvedRefs {
  stillsSpecName: string | null;
  videosSpecName: string | null;
  stillsConventionName: string | null;
  videosConventionName: string | null;
  evConventionSummary: string | null;
  evConventionCount: number;
}

export const resolveFormulaRefs = (
  formula: OutputFormula,
  specs: readonly FileSpecification[],
  conventions: readonly FilenameConvention[],
): FormulaResolvedRefs => {
  const specMap = buildNameLookup(specs);
  const conventionMap = buildNameLookup(conventions);

  const stillsSpecId = formulaStillsSpecId(formula.value);
  const videosSpecId = formulaVideosSpecId(formula.value);
  const stillsConventionId = formulaStillsConventionId(formula.value);
  const videosConventionId = formulaVideosConventionId(formula.value);
  const evIds = formulaEvConventionIds(formula.value);

  return {
    stillsSpecName: stillsSpecId === null ? null : lookupName(specMap, stillsSpecId),
    videosSpecName: videosSpecId === null ? null : lookupName(specMap, videosSpecId),
    stillsConventionName:
      stillsConventionId === null ? null : lookupName(conventionMap, stillsConventionId),
    videosConventionName:
      videosConventionId === null ? null : lookupName(conventionMap, videosConventionId),
    evConventionSummary: evIds.length === 0 ? null : orderedEvConventionNames(conventionMap, evIds),
    evConventionCount: evIds.length,
  };
};
